import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Bell, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { createApiClient } from '../../api/client';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const dropdownRef = useRef(null);
  const { token, logout } = useAuth();
  const apiClient = useMemo(() => createApiClient(token, logout), [token, logout]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setIsLoading(true);
        const res = await apiClient('/notifications/unread');
        if (res.ok) {
          const data = await res.json();
          setNotifications(data);
        } else {
            throw new Error("Failed to load notifications");
        }
      } catch (err) {
        console.error('Failed to fetch notifications', err);
        // Fallback for development if needed
        if (process.env.NODE_ENV === 'development') {
           setNotifications([
             { id: 1, title: "New Campaign Assigned", message: "You have been assigned to the FB Ads campaign.", date: "Oct 12, 2023" },
             { id: 2, title: "Database Sync Complete", message: "Your database has been successfully synced.", date: "Oct 13, 2023" }
           ]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotifications();
  }, [apiClient]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id) => {
    // Optimistic UI update
    setNotifications((prev) => prev.filter(n => n.id !== id));
    
    try {
      await apiClient(`/notifications/mark-read/${id}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Failed to mark as read', err);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-text-secondary hover:text-primary transition-colors bg-bg-soft rounded-full border border-border-light shadow-small focus:outline-none"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {notifications.length > 0 && (
          <span className="absolute top-0 right-0 h-3 w-3 bg-[#E53E3E] rounded-full translate-x-1/4 -translate-y-1/4 border border-bg-card"></span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-bg-card rounded-card shadow-floating border border-border-light overflow-hidden z-50 origin-top-right"
          >
            <div className="px-4 py-3 border-b border-border-light bg-bg-soft/50">
              <h3 className="text-[14px] font-bold text-text-primary tracking-tight">Notifications</h3>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center text-text-muted">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : notifications.length > 0 ? (
                <div className="flex flex-col">
                  {notifications.map((notification) => (
                    <motion.div 
                      key={notification.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, height: 0 }}
                      className="p-4 border-b border-border-light last:border-b-0 hover:bg-bg-soft transition-colors cursor-pointer"
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <h4 className="text-[13px] font-semibold text-text-primary">{notification.title}</h4>
                      <p className="text-[12px] text-text-secondary mt-1 line-clamp-2 leading-relaxed">{notification.message}</p>
                      <p className="text-[10px] text-text-muted uppercase tracking-wider mt-2">{notification.date}</p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="p-8 flex flex-col items-center justify-center text-center">
                  <CheckCircle className="w-8 h-8 text-text-muted mb-3 opacity-50" />
                  <p className="text-[14px] font-bold text-text-primary">You're all caught up!</p>
                  <p className="text-[12px] text-text-secondary mt-1">No new notifications.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
